import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { setIsAccountPanelExpanded } from 'ducks/layout/layoutSlice';
import type { Variants } from 'motion/react';
import { motion } from 'motion/react';
import { useDispatch } from 'react-redux';
import type { PopupActions } from 'reactjs-popup/dist/types';

import Clickable from '@polyai/ui/components/atoms/Clickable';
import { LogoIcon } from '@polyai/ui/components/atoms/Icons';
import LogoText from '@polyai/ui/components/atoms/Logo/LogoText';
import ScrollIndicator from '@polyai/ui/components/atoms/ScrollIndicator';
import Skeleton from '@polyai/ui/components/atoms/Skeleton';
import { Stack } from '@polyai/ui/components/atoms/Stack';
import Text from '@polyai/ui/components/atoms/Text';
import InPlaceSearchField from '@polyai/ui/components/molecules/InPlaceSearchField';
import {
  SidebarScrollableContent,
  SidebarStickyFooter,
  SidebarStickyHeader,
} from '@polyai/ui/components/molecules/StickySidebar';
import TextOverflow from '@polyai/ui/components/molecules/TextOverflow';
import sizes from '@polyai/ui/styles/config/sizes';
import { cn } from '@polyai/ui/utils';

import useBannerOffset from 'hooks/useBannerOffset';
import { useIsPlgProd } from 'hooks/useIsPlgProd';
import * as Styled from './AccountSidePanel.styled';
import HelpCenterMenu from './components/HelpCenterMenu';
import UserMenu from './components/UserMenu';

export type SidebarAccount = {
  id: string;
  name: string;
  abbreviation: string;
};

export type AccountSidePanelProps = {
  accounts: SidebarAccount[];
  isLoadingAccounts?: boolean;
  activeAccountId: string;
};

const skeletonVariants: Variants = {
  animate: {
    opacity: [0.2, 0.5, 0.2],
    transition: {
      duration: 2.5,
      repeat: Infinity,
      ease: 'easeInOut' as const,
    },
  },
};

const AccountSidePanel: React.FC<AccountSidePanelProps> = ({
  accounts,
  isLoadingAccounts: originalIsLoadingAccounts,
  activeAccountId,
}) => {
  const isLoadingAccounts = originalIsLoadingAccounts; // TODO: set true to test loading state
  const bannerOffset = useBannerOffset();
  const isPlgProd = useIsPlgProd();
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [fadeExpanded, setFadeExpanded] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const helpMenuRef = useRef<PopupActions>(null);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const dispatch = useDispatch();

  // Handle user menu open change
  const handleUserMenuOpenChange = useCallback((open: boolean) => {
    setIsUserMenuOpen(open);
  }, []);

  const filteredAccounts = useMemo(() => {
    let filtered;
    if (searchInput) {
      filtered = accounts.filter(a =>
        a.name.toLowerCase().includes(searchInput.toLowerCase()),
      );
      return filtered;
    }
    return accounts;
  }, [accounts, searchInput]);

  // Memoized event handlers to prevent unnecessary re-renders
  const handleMouseEnter = useCallback(() => setIsMouseOver(true), []);
  const handleMouseLeave = useCallback(() => setIsMouseOver(false), []);
  const handleSearchFocus = useCallback(() => setIsSearchFocused(true), []);
  const handleSearchBlur = useCallback(() => setIsSearchFocused(false), []);
  const handleSetSearchInput = useCallback(
    (text: string) => setSearchInput(text),
    [],
  );

  // Consolidated timer logic for expand/collapse
  useEffect(() => {
    const shouldExpand = isMouseOver || isSearchFocused || isUserMenuOpen;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    timerRef.current = setTimeout(() => {
      if (!shouldExpand) {
        setSearchInput('');
        setIsUserMenuOpen(false);
      }
      setIsExpanded(shouldExpand);
      dispatch(setIsAccountPanelExpanded(shouldExpand));
      // Preserve stagger effect with small delay for fade
      setTimeout(() => {
        setFadeExpanded(shouldExpand);
        if (!shouldExpand) {
          const helpMenu = helpMenuRef.current;
          helpMenu?.close();
        }
      }, 70);
    }, 300);
  }, [isMouseOver, isSearchFocused, isUserMenuOpen, dispatch]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (isLoadingAccounts) {
      timer = setTimeout(() => setShowSkeleton(true), 300); // 300ms delay
    } else {
      setShowSkeleton(false);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isLoadingAccounts]);

  return (
    <>
      <Stack style={{ width: sizes.accountSidebarDefaultWidth }} />
      <Styled.FadingSidebar
        $open={fadeExpanded}
        actInline={false}
        backgroundColor="backgroundPrimary"
        headerOffset={bannerOffset}
        id="account-panel"
        isMainNavOpen={false}
        open
        width={
          isExpanded
            ? sizes.accountSidebarExpandedWidth
            : sizes.accountSidebarDefaultWidth
        }
        scrollIndicatorHeight={40}
      >
        {/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing complex logic */}
        {({ scrollableRef, isMoreScrollTop, isMoreScrollBottom }) => (
          <SidebarScrollableContent
            ref={scrollableRef}
            $flex
            $hideScrollbar
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <SidebarStickyHeader
              $backgroundColor="backgroundPrimary"
              $borderColor="backgroundPrimary"
            >
              <Stack
                align={isExpanded ? 'flex-start' : 'center'}
                direction="column"
                className="p-sm1"
                spacing="sm1"
              >
                <InPlaceSearchField
                  autoFocus={false}
                  open={isExpanded}
                  primaryColor="iconPrimaryInverse"
                  searchInput={searchInput}
                  secondaryColor="iconSecondaryInverse"
                  setSearchInput={handleSetSearchInput}
                  shrinkIcon={false}
                  fullWidth
                  onBlur={handleSearchBlur}
                  onFocus={handleSearchFocus}
                />
              </Stack>
              {!!isMoreScrollTop && (
                <ScrollIndicator
                  initial={false}
                  position="top"
                  style={{ height: isMoreScrollTop, top: '100%' }}
                />
              )}
            </SidebarStickyHeader>
            <Stack
              align="flex-start"
              direction="column"
              flex="1 0 auto"
              position="relative"
              spacing="none"
            >
              {showSkeleton ? (
                <Stack
                  align={isExpanded ? 'flex-start' : 'center'}
                  direction="column"
                  spacing="sm1"
                  width="100%"
                >
                  {[...new Array(6)].map((_, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: static list, index key is safe
                    <Styled.SkeletonRow key={i} $isExpanded={isExpanded}>
                      <motion.div
                        animate="animate"
                        style={{ width: '100%' }}
                        variants={skeletonVariants}
                      >
                        <Styled.SkeletonStack $isExpanded={isExpanded}>
                          <Skeleton borderRadius={8} height={40} width={40} />
                          {isExpanded && (
                            <Skeleton
                              borderRadius={4}
                              height={16}
                              width={120}
                            />
                          )}
                        </Styled.SkeletonStack>
                      </motion.div>
                    </Styled.SkeletonRow>
                  ))}
                </Stack>
              ) : filteredAccounts?.length ? (
                // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing complex logic
                filteredAccounts.map(account => {
                  const isSelected = account.id === activeAccountId;

                  return (
                    <Clickable
                      key={account.id}
                      aria-label={account.name}
                      data-dd-action="Account: open"
                      data-test-id="select-account-btn"
                      style={{
                        width: '100%',
                        minWidth: 40,
                      }}
                      to={`/${account.id}`}
                    >
                      <Styled.AccountRow
                        $isExpanded={isExpanded}
                        align="center"
                        justify={isExpanded ? 'flex-start' : 'center'}
                        overflow="hidden"
                        className={cn('py-xs3', isExpanded && 'px-sm1')}
                        spacing="xs3"
                        width="100%"
                      >
                        <Stack
                          align="center"
                          background={
                            isSelected
                              ? 'surfaceNavSelection'
                              : 'backgroundAccentInverse'
                          }
                          borderColor={
                            isSelected ? 'transparent' : 'borderStrongInverse'
                          }
                          flex="0 0 40px"
                          height={40}
                          justify="center"
                          radius="radiusXSmall"
                          width={40}
                        >
                          <Text
                            color={
                              isSelected ? 'textPrimary' : 'textPrimaryInverse'
                            }
                            size="body.regular"
                            weight="bold"
                          >
                            {account.abbreviation}
                          </Text>
                        </Stack>
                        {isExpanded && (
                          <TextOverflow
                            className="accountName"
                            style={{ cursor: 'pointer' }}
                            textProps={{
                              color: 'textPrimaryInverse',
                              size: 'body.regular',
                              weight: 'semibold',
                            }}
                            tooltipProps={{ disabled: true }}
                          >
                            {account.name}
                          </TextOverflow>
                        )}
                      </Styled.AccountRow>
                    </Clickable>
                  );
                })
              ) : !isLoadingAccounts && !showSkeleton ? (
                <Stack direction="column" className="px-sm1" spacing="xs2">
                  <Text color="textPrimaryInverse" variant="bodySemibold">
                    No workspaces found
                  </Text>
                  <Text color="textSecondaryInverse" variant="bodySmall">
                    If the workspace exists, you may have misspelled its name,
                    or you may not have joined it yet.
                  </Text>
                </Stack>
              ) : null}
            </Stack>
            <SidebarStickyFooter
              $backgroundColor="backgroundPrimary"
              $borderColor="backgroundPrimary"
            >
              {!!isMoreScrollBottom && (
                <ScrollIndicator
                  initial={false}
                  position="bottom"
                  style={{ height: isMoreScrollBottom, bottom: '100%' }}
                />
              )}
              <Stack direction="column" spacing="xxs2" width="100%">
                <Stack
                  align="stretch"
                  borderColor="borderSubtleInverse"
                  borderWidth="1px 0 0"
                  direction="column"
                  width="100%"
                >
                  <UserMenu
                    isExpanded={isExpanded}
                    open={isUserMenuOpen}
                    onPopoverOpenChange={handleUserMenuOpenChange}
                  />
                  <HelpCenterMenu
                    isExpanded={isExpanded}
                    popupRef={helpMenuRef}
                  />
                </Stack>
                <Styled.NoSvgShrink
                  align="center"
                  justify={isExpanded ? 'flex-start' : 'center'}
                  className="p-xs2 pb-sm1"
                  spacing="xs2"
                  style={{ paddingLeft: isExpanded ? 14 : undefined }}
                  width="100%"
                >
                  <LogoIcon color="iconBrand" size="lg" />
                  {isExpanded &&
                    (isPlgProd ? (
                      <span
                        className="text-body-regular font-semibold whitespace-nowrap"
                        style={{ color: '#ffffff' }}
                      >
                        Agent Studio
                      </span>
                    ) : (
                      <LogoText className="logoText" />
                    ))}
                </Styled.NoSvgShrink>
              </Stack>
            </SidebarStickyFooter>
          </SidebarScrollableContent>
        )}
      </Styled.FadingSidebar>
    </>
  );
};

export default AccountSidePanel;
